/**
* แปลและรับรองเอกสารทุกภาษา — Wave 2 curated Q&A (hand-written, Thai).
*
* ทุกคำตอบยึดข้อเท็จจริงที่ตรวจสอบได้:
* - สายรับรองปัจจุบัน: ผู้ออกเอกสาร/ผู้แปล → Notarial Services Attorney (ถ้าจำเป็น)
* → กองสัญชาติและนิติกรณ์ กรมการกงสุล → สถานทูตปลายทาง (ถ้าประเทศนั้นยังกำหนด)
* - ไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผล 28 กุมภาพันธ์ 2027 (ดู src/data/legal-facts.ts)
* - ค่าธรรมเนียมนิติกรณ์กรมการกงสุล: 200 บาท/ตราประทับ (แบบปกติ) และ 400 บาท/ตราประทับ (แบบด่วน)
* ค่าธรรมเนียมเป็นของหน่วยงานราชการและอาจปรับเปลี่ยนได้ ควรตรวจสอบก่อนยื่นทุกครั้ง
*
* ไม่ระบุราคาบริการของสำนักงานในคำตอบ (คุมตามกฎ compliance ของโปรเจกต์)
*/
import type { MltQA } from "./questions-500";

const PHONE = "094-895-8999";
const CTA = `Thai Notary Law · โทร ${PHONE} · LINE @Thainotary — แปล รับรองคำแปล และเดินเรื่องนิติกรณ์ครบวงจร`;
const FEE_NOTE =
  "ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลตามอัตราราชการ (ปกติ 200 บาท/ตราประทับ, ด่วน 400 บาท/ตราประทับ) เป็นค่าธรรมเนียมของหน่วยงาน ไม่ใช่ค่าบริการแปล และอาจเปลี่ยนแปลงได้ ควรตรวจสอบอัตราล่าสุดก่อนยื่น";
const APOSTILLE_NOTE =
  "ปัจจุบันประเทศไทยยังไม่ออกตรา Apostille — อนุสัญญากรุงเฮก ค.ศ. 1961 จะมีผลบังคับใช้กับไทยวันที่ 28 กุมภาพันธ์ 2027 ระหว่างนี้ยังใช้สายรับรองเดิม คือ กรมการกงสุล → สถานทูตปลายทาง (เฉพาะประเทศที่ยังกำหนด)";

export const MLT_QA_WAVE2: MltQA[] = [
  {
    slug: "mlt-w2-ใครรับรองคำแปลได้บ้าง",
    q: "ในประเทศไทย ใครมีอำนาจรับรองคำแปลเอกสารได้บ้าง?",
    aShort:
      "ไทยไม่มีระบบ 'นักแปลสาบานตน' (sworn translator) ตามกฎหมายแบบยุโรป ผู้แปลหรือสำนักงานแปลจึงรับรองความถูกต้องของคำแปลด้วยตนเอง แล้วยกระดับความน่าเชื่อถือด้วยการรับรองลายมือชื่อโดยทนายผู้ทำคำรับรองลายมือชื่อ และ/หรือรับรองนิติกรณ์ที่กรมการกงสุล",
    aLong:
      "ลำดับชั้นที่ใช้จริง: (1) ผู้แปล/สำนักงานออก Certificate of Accuracy ระบุชื่อ คุณสมบัติ คู่ภาษา วันที่ พร้อมลายเซ็นและตราประทับ (2) หากปลายทางต้องการยืนยันตัวผู้ลงนาม ให้ทนายความที่ขึ้นทะเบียนเป็น Notarial Services Attorney กับสภาทนายความรับรองลายมือชื่อผู้แปล (3) หากต้องใช้กับหน่วยงานต่างประเทศหรือสถานทูต ให้ยื่นรับรองนิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล · " +
      APOSTILLE_NOTE +
      " · " +
      CTA,
    synonyms: [
      "ใครรับรองคำแปลได้",
      "sworn translator thailand มีไหม",
      "who can certify translation in thailand",
    ],
    cluster: "certification",
  },
  {
    slug: "mlt-w2-กรมการกงสุลรับรองคำแปลเงื่อนไข",
    q: "กรมการกงสุลรับรองคำแปลในเงื่อนไขใดบ้าง?",
    aShort:
      "กรมการกงสุลรับรองนิติกรณ์คำแปลไทย–อังกฤษ (และอังกฤษ–ไทย) โดยตรวจว่าคำแปลตรงกับต้นฉบับ ต้นฉบับเป็นเอกสารที่ออกโดยหน่วยงานที่ตรวจสอบได้ และการสะกดชื่อ-นามสกุลตรงกับหนังสือเดินทาง",
    aLong:
      "จุดที่ถูกตีกลับบ่อย: สะกดชื่อไม่ตรงพาสปอร์ต, เลขที่เอกสาร/วันที่ในคำแปลไม่ตรงต้นฉบับ, ใช้สำเนาที่ไม่ได้รับรองสำเนาถูกต้องจากหน่วยงานผู้ออก, ตกหล่นตราประทับหรือหมายเหตุท้ายเอกสาร · สำหรับภาษาอื่นนอกเหนือไทย–อังกฤษ แนวปฏิบัติทั่วไปคือแปลเป็นภาษาอังกฤษก่อนหนึ่งชั้น แล้วจึงให้สถานทูตปลายทางรับรองต่อ · " +
      FEE_NOTE +
      " · " +
      CTA,
    synonyms: [
      "กรมการกงสุลรับรองคำแปล เงื่อนไข",
      "mfa legalisation translation requirements",
      "รับรองนิติกรณ์คำแปล",
    ],
    cluster: "authority",
  },
  {
    slug: "mlt-w2-ลำดับขั้นตอนแปลส่งต่างประเทศ",
    q: "เอกสารไทยที่จะใช้ต่างประเทศ ต้องแปลและรับรองตามลำดับอย่างไร?",
    aShort:
      "ลำดับมาตรฐาน: ขอเอกสารต้นฉบับ/สำเนาที่รับรองจากหน่วยงานผู้ออก → แปลและออกหนังสือรับรองคำแปล → รับรองลายมือชื่อโดย Notary (ถ้าปลายทางกำหนด) → รับรองนิติกรณ์ที่กรมการกงสุล → รับรองสถานทูตประเทศปลายทางในไทย (ถ้าประเทศนั้นยังกำหนด)",
    aLong:
      "การสลับลำดับเป็นสาเหตุตีกลับอันดับต้น ๆ เช่น นำไปสถานทูตก่อนผ่านกรมการกงสุล หรือแปลจากสำเนาที่ยังไม่รับรอง · เอกสารทะเบียนราษฎร (สูติบัตร ทะเบียนบ้าน ทะเบียนสมรส) ควรขอฉบับคัดรับรองใหม่จากสำนักทะเบียนก่อนแปล เพื่อให้วันที่คัดรับรองใหม่ · " +
      APOSTILLE_NOTE +
      " · " +
      CTA,
    synonyms: [
      "ขั้นตอนแปลเอกสารส่งต่างประเทศ",
      "legalisation chain thailand order",
      "แปลแล้วรับรองที่ไหนก่อน",
    ],
    cluster: "process",
  },
  {
    slug: "mlt-w2-สะกดชื่อตามพาสปอร์ต",
    q: "ทำไมการสะกดชื่อในคำแปลต้องตรงกับหนังสือเดินทาง?",
    aShort:
      "เพราะหน่วยงานปลายทางใช้ชื่อในคำแปลจับคู่กับหนังสือเดินทางและฐานข้อมูลของตน หากสะกดต่างกันแม้ตัวอักษรเดียว จะถือว่าเป็นคนละบุคคลและตีกลับทันที",
    aLong:
      "แนวปฏิบัติ: ส่งสำเนาหน้าพาสปอร์ตของทุกคนที่ปรากฏชื่อในเอกสารมาพร้อมงานแปลตั้งแต่ต้น หากไม่มีพาสปอร์ต (เช่น ผู้เยาว์) ให้ระบุการถอดอักษรที่ต้องการเป็นลายลักษณ์อักษร และใช้การสะกดเดียวกันในทุกเอกสารของชุดเดียวกัน · การแก้การสะกดหลังผ่านนิติกรณ์แล้วต้องเริ่มกระบวนการรับรองใหม่ทั้งชุด · " +
      CTA,
    synonyms: [
      "สะกดชื่อในคำแปลไม่ตรงพาสปอร์ต",
      "name spelling must match passport translation",
      "transliteration ชื่อไทยเป็นอังกฤษ",
    ],
    cluster: "troubleshoot",
  },
  {
    slug: "mlt-w2-แปลเอกสารต่างประเทศมาใช้ในไทย",
    q: "เอกสารที่ออกจากต่างประเทศ จะนำมาใช้ในไทยต้องทำอย่างไร?",
    aShort:
      "ต้องรับรองจากประเทศต้นทางก่อน (หน่วยงานผู้ออก → กระทรวงการต่างประเทศของประเทศนั้น → สถานเอกอัครราชทูต/สถานกงสุลไทยในประเทศนั้น) แล้วจึงนำเข้ามาแปลเป็นภาษาไทยและรับรองนิติกรณ์ที่กรมการกงสุล",
    aLong:
      "จนกว่าอนุสัญญา Apostille จะมีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2027 เอกสารต่างประเทศส่วนใหญ่ยังต้องผ่านการรับรองโดยสถานทูต/สถานกงสุลไทยในต่างประเทศ การมี Apostille จากประเทศต้นทางเพียงอย่างเดียวจึงยังไม่เพียงพอสำหรับหน่วยงานไทยหลายแห่งในช่วงเปลี่ยนผ่าน · หน่วยงานไทยที่ตรวจเข้ม ได้แก่ สำนักงานเขต/อำเภอ กรมที่ดิน กรมพัฒนาธุรกิจการค้า และศาล · " +
      CTA,
    synonyms: [
      "เอกสารต่างประเทศใช้ในไทย ต้องรับรองอะไร",
      "foreign document use in thailand legalisation",
      "แปลเอกสารต่างประเทศเป็นไทย",
    ],
    cluster: "process",
  },
  {
    slug: "mlt-w2-แปลสองชั้นภาษาที่สาม",
    q: "ถ้าเอกสารเป็นภาษาที่สาม (ไม่ใช่ไทย/อังกฤษ) ต้องแปลกี่ชั้น?",
    aShort:
      "โดยทั่วไปแปลเป็นภาษาอังกฤษหรือภาษาไทยหนึ่งชั้นเพื่อให้หน่วยงานตรวจสอบได้ แล้วจึงแปลต่อเป็นภาษาปลายทางถ้าประเทศนั้นกำหนดให้ใช้ภาษาราชการของตน",
    aLong:
      "ตัวอย่าง: เอกสารภาษาเยอรมันที่จะใช้ในไทย → แปลเยอรมัน–ไทย พร้อมหนังสือรับรองคำแปล และผ่านการรับรองจากต้นทางแล้ว · เอกสารไทยที่จะใช้ในญี่ปุ่น → บางหน่วยงานรับคำแปลอังกฤษ บางแห่งขอภาษาญี่ปุ่น ต้องยืนยันกับผู้รับปลายทางก่อนเริ่มงาน เพื่อไม่ต้องแปลซ้ำและเสียค่าธรรมเนียมนิติกรณ์ซ้ำ · " +
      CTA,
    synonyms: [
      "แปลภาษาที่สามต้องแปลกี่รอบ",
      "third language translation relay thailand",
      "pivot translation english bridge",
    ],
    cluster: "language",
  },
  {
    slug: "mlt-w2-คำแปลอายุใช้งาน",
    q: "คำแปลรับรองมีอายุใช้งานหรือไม่?",
    aShort:
      "ตัวคำแปลไม่มีวันหมดอายุตามกฎหมาย แต่หน่วยงานปลายทางมักกำหนดว่าตราประทับรับรองและเอกสารต้นฉบับต้องออกใหม่ภายใน 3–6 เดือน จึงควรตรวจข้อกำหนดของผู้รับก่อนยื่น",
    aLong:
      "เอกสารที่มักถูกกำหนดอายุ ได้แก่ หนังสือรับรองความเป็นโสด หนังสือรับรองบริษัท ทะเบียนบ้าน และใบรับรองประวัติอาชญากรรม · แนวปฏิบัติที่ปลอดภัย: คัดรับรองต้นฉบับใหม่ แล้วแปลและรับรองในรอบเดียวกันก่อนยื่นภายใน 3 เดือน · " +
      CTA,
    synonyms: [
      "คำแปลรับรองหมดอายุไหม",
      "validity of certified translation thailand",
      "ตราประทับกงสุลอายุกี่เดือน",
    ],
    cluster: "troubleshoot",
  },
  {
    slug: "mlt-w2-รูปแบบคำแปลที่ยอมรับ",
    q: "คำแปลที่หน่วยงานยอมรับต้องมีรูปแบบอย่างไร?",
    aShort:
      "ต้องแปลครบทุกข้อความรวมถึงตราประทับ ลายเซ็น หมายเหตุ และข้อความท้ายเอกสาร วางรูปแบบให้ตรงกับต้นฉบับ พร้อมหนังสือรับรองความถูกต้องของคำแปลแนบท้าย",
    aLong:
      "รายละเอียดที่ตรวจบ่อย: ระบุ [ตราประทับ] [ลายเซ็นอ่านไม่ออก] แทนการละไว้, คงเลขที่เอกสารและวันที่ตามต้นฉบับ, ไม่เพิ่มหรือย่อข้อความ, ระบุจำนวนหน้าและเรียงหน้าให้ตรงกัน, เย็บติดกับสำเนาต้นฉบับที่ใช้แปลและประทับตราคร่อมรอยต่อ · " +
      CTA,
    synonyms: [
      "รูปแบบคำแปลรับรองที่ถูกต้อง",
      "certified translation format requirements",
      "แปลตราประทับด้วยไหม",
    ],
    cluster: "documents",
  },
  {
    slug: "mlt-w2-แปลเอกสารวีซ่าเชงเก้น",
    q: "แปลเอกสารยื่นวีซ่าเชงเก้น ต้องรับรองถึงชั้นไหน?",
    aShort:
      "สถานทูตกลุ่มเชงเก้นส่วนใหญ่รับคำแปลภาษาอังกฤษพร้อมหนังสือรับรองคำแปลจากผู้แปล บางประเทศกำหนดเพิ่มให้ผ่านกรมการกงสุลหรือรับรองโดยสถานทูตของตนเอง ต้องตรวจ checklist ของแต่ละสถานทูตก่อนยื่น",
    aLong:
      "ข้อกำหนดต่างกันจริงในทางปฏิบัติ เช่น เอกสารการเงินและหนังสือรับรองการทำงานมักรับคำแปลอังกฤษธรรมดา ขณะที่เอกสารสถานะบุคคล (สูติบัตร ทะเบียนสมรส) ที่ใช้ประกอบวีซ่าติดตามครอบครัวมักต้องผ่านนิติกรณ์กรมการกงสุลและสถานทูต · เราตรวจ checklist ล่าสุดของสถานทูตที่จะยื่นให้ก่อนเริ่มงานทุกครั้ง · " +
      FEE_NOTE +
      " · " +
      CTA,
    synonyms: [
      "แปลเอกสารวีซ่าเชงเก้น",
      "schengen visa document translation thailand",
      "สถานทูตต้องการคำแปลแบบไหน",
    ],
    cluster: "authority",
  },
  {
    slug: "mlt-w2-แปลงบการเงินและเอกสารบริษัท",
    q: "แปลงบการเงินและเอกสารบริษัทต้องระวังอะไรเป็นพิเศษ?",
    aShort:
      "ต้องคงตัวเลข สกุลเงิน หน่วย และชื่อบัญชีตามมาตรฐานบัญชีให้ตรงต้นฉบับ ใช้ชื่อบริษัทภาษาอังกฤษตามที่จดทะเบียนกับกรมพัฒนาธุรกิจการค้า และแปลหมายเหตุประกอบงบให้ครบ",
    aLong:
      "จุดผิดพลาดที่พบบ่อย: ใช้ชื่อบริษัทภาษาอังกฤษที่ไม่ตรงกับหนังสือรับรอง DBD, แปลชื่อรายการบัญชีไม่ตรงมาตรฐาน, ตัดหมายเหตุประกอบงบออก, ไม่แปลรายงานผู้สอบบัญชี · สำหรับการยื่นต่างประเทศ ควรให้กรรมการผู้มีอำนาจลงนามรับรองสำเนา แล้วจึงให้ Notary รับรองลายมือชื่อก่อนเข้ากรมการกงสุล · " +
      CTA,
    synonyms: [
      "แปลงบการเงินบริษัท",
      "financial statement translation thailand",
      "แปลหนังสือรับรองบริษัท dbd",
    ],
    cluster: "specialized",
  },
  {
    slug: "mlt-w2-แปลเอกสารการแพทย์",
    q: "แปลเอกสารทางการแพทย์ต้องใช้ผู้แปลเฉพาะทางหรือไม่?",
    aShort:
      "ควรใช้ผู้แปลที่คุ้นเคยศัพท์การแพทย์และรหัสมาตรฐาน เช่น ICD เพราะการแปลชื่อโรค ขนาดยา และผลตรวจผิดพลาดมีผลต่อการรักษาและการพิจารณาวีซ่าหรือประกัน",
    aLong:
      "แนวปฏิบัติ: คงชื่อยาสามัญ (generic name) และหน่วยวัดตามต้นฉบับ, ระบุค่าอ้างอิงของห้องปฏิบัติการให้ครบ, ไม่ตีความผลตรวจเพิ่มเติมจากที่แพทย์เขียน, หากลายมือแพทย์อ่านไม่ออกให้ขอฉบับพิมพ์จากโรงพยาบาลแทนการเดา · เอกสารที่ต้องยื่นหน่วยงานต่างประเทศให้โรงพยาบาลรับรองสำเนาก่อนแปล · " +
      CTA,
    synonyms: [
      "แปลใบรับรองแพทย์",
      "medical report translation thailand",
      "แปลผลตรวจสุขภาพยื่นวีซ่า",
    ],
    cluster: "specialized",
  },
  {
    slug: "mlt-w2-แปลเอกสารศาลและสัญญา",
    q: "แปลเอกสารคดีความและสัญญา ต้องระวังอะไร?",
    aShort:
      "ศัพท์กฎหมายต้องแปลตามระบบกฎหมายของเอกสารต้นฉบับ ไม่ใช่หาคำที่ใกล้เคียงในอีกระบบ และควรมีทนายความตรวจศัพท์ก่อนยื่นศาลหรือใช้ประกอบสัญญา",
    aLong:
      "ตัวอย่างที่ต้องระวัง: คำว่า affidavit, deed, trust, equity ไม่มีสถาบันตรงกันในกฎหมายไทย จึงควรแปลเชิงอธิบายพร้อมคงคำเดิมในวงเล็บ · สัญญาสองภาษาให้ระบุชัดว่าภาษาใดเป็นฉบับที่ใช้บังคับ (governing language) เพื่อลดข้อพิพาทในการตีความ · " +
      CTA,
    synonyms: [
      "แปลสัญญาสองภาษา",
      "legal contract translation thailand",
      "แปลเอกสารยื่นศาลไทย",
    ],
    cluster: "specialized",
  },
  {
    slug: "mlt-w2-คำแปลดิจิทัลกับตัวจริง",
    q: "คำแปลไฟล์ PDF ที่ลงลายเซ็นดิจิทัล ใช้แทนฉบับตัวจริงได้ไหม?",
    aShort:
      "ขึ้นกับผู้รับ หน่วยงานราชการไทยและกรมการกงสุลยังต้องการเอกสารกระดาษที่มีลายเซ็นและตราประทับจริง ส่วนมหาวิทยาลัยหรือหน่วยงานเอกชนบางแห่งรับไฟล์ที่ลงลายมือชื่ออิเล็กทรอนิกส์ได้",
    aLong:
      "แนวปฏิบัติที่ปลอดภัย: ส่งไฟล์ให้ผู้รับตรวจล่วงหน้าเพื่อความรวดเร็ว และจัดทำฉบับกระดาษที่ผ่านนิติกรณ์ควบคู่ไว้เสมอเมื่อปลายทางเป็นหน่วยงานราชการ · การรับรองนิติกรณ์ที่กรมการกงสุลดำเนินการกับเอกสารกระดาษ · " +
      FEE_NOTE +
      " · " +
      CTA,
    synonyms: [
      "คำแปลไฟล์ pdf ใช้ได้ไหม",
      "digital signed translation accepted thailand",
      "e-signature คำแปล",
    ],
    cluster: "troubleshoot",
  },
  {
    slug: "mlt-w2-ถูกตีกลับทำอย่างไร",
    q: "ถ้าคำแปลถูกตีกลับ ต้องเริ่มใหม่ทั้งหมดหรือไม่?",
    aShort:
      "ไม่เสมอไป ถ้าเป็นการแก้ไขในตัวคำแปลก่อนผ่านนิติกรณ์ แก้แล้วยื่นใหม่ได้ แต่ถ้าตราประทับรับรองออกไปแล้ว ต้องจัดทำคำแปลฉบับใหม่และรับรองใหม่ทั้งชั้น",
    aLong:
      "สาเหตุตีกลับที่พบบ่อย 5 อันดับ: (1) ชื่อสะกดไม่ตรงพาสปอร์ต (2) แปลจากสำเนาที่ไม่ได้รับรอง (3) ตกหล่นตราประทับ/หมายเหตุ (4) ลำดับการรับรองผิดขั้น (5) เอกสารต้นฉบับเก่ากว่าที่ปลายทางกำหนด · เก็บใบเสร็จและเลขที่รับเรื่องไว้ทุกครั้งเพื่อใช้อ้างอิงเมื่อต้องยื่นซ้ำ · " +
      CTA,
    synonyms: [
      "คำแปลถูกตีกลับแก้อย่างไร",
      "translation rejected by embassy thailand",
      "แก้ไขคำแปลหลังรับรอง",
    ],
    cluster: "troubleshoot",
  },
  {
    slug: "mlt-w2-ความลับข้อมูลงานแปล",
    q: "งานแปลเอกสารส่วนบุคคลมีการคุ้มครองข้อมูลอย่างไร?",
    aShort:
      "ผู้ให้บริการแปลถือเป็นผู้ควบคุม/ผู้ประมวลผลข้อมูลส่วนบุคคลตาม พ.ร.บ.คุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 จึงต้องเก็บรักษาเอกสาร จำกัดผู้เข้าถึง และลบข้อมูลเมื่อพ้นวัตถุประสงค์",
    aLong:
      "แนวปฏิบัติที่ควรถามผู้ให้บริการ: ช่องทางรับ-ส่งไฟล์ที่เข้ารหัส, ระยะเวลาเก็บไฟล์และนโยบายลบ, การทำข้อตกลงรักษาความลับ (NDA) สำหรับเอกสารบริษัทหรือคดีความ, การไม่นำเอกสารเข้าเครื่องมือแปลอัตโนมัติสาธารณะ · " +
      CTA,
    synonyms: [
      "ข้อมูลส่วนบุคคลงานแปล pdpa",
      "translation confidentiality thailand nda",
      "ส่งเอกสารแปลปลอดภัยไหม",
    ],
    cluster: "process",
  },
  {
    slug: "mlt-w2-machine-translation-ใช้ได้ไหม",
    q: "ใช้ Google Translate หรือ AI แปลแล้วให้รับรองได้ไหม?",
    aShort:
      "ไม่แนะนำ เพราะผู้ลงนามรับรองต้องรับผิดชอบความถูกต้องของคำแปล คำแปลอัตโนมัติมักผิดที่ชื่อเฉพาะ วันที่ ตัวเลข และศัพท์กฎหมาย ซึ่งเป็นจุดที่หน่วยงานตรวจเข้มที่สุด",
    aLong:
      "หากใช้เครื่องช่วยแปลในขั้นตอนร่าง ต้องมีนักแปลมนุษย์ตรวจแก้ทุกบรรทัดเทียบต้นฉบับก่อนออกหนังสือรับรองคำแปล และไม่ควรอัปโหลดเอกสารส่วนบุคคลเข้าเครื่องมือสาธารณะเพราะขัดกับหลักคุ้มครองข้อมูลส่วนบุคคล · " +
      CTA,
    synonyms: [
      "ai แปลเอกสารราชการได้ไหม",
      "machine translation certified accepted",
      "google translate ใช้ยื่นราชการได้ไหม",
    ],
    cluster: "compare",
  },
  {
    slug: "mlt-w2-เตรียมเอกสารก่อนส่งแปล",
    q: "ต้องเตรียมอะไรก่อนส่งเอกสารมาแปลเพื่อให้เร็วที่สุด?",
    aShort:
      "เตรียมต้นฉบับหรือสำเนาที่รับรองแล้ว สแกนสีความละเอียดสูงครบทุกหน้า สำเนาหน้าพาสปอร์ตของทุกชื่อในเอกสาร ระบุประเทศ/หน่วยงานปลายทาง และภาษาที่ปลายทางต้องการ",
    aLong:
      "ข้อมูลปลายทางสำคัญที่สุด เพราะกำหนดว่าต้องรับรองถึงชั้นใดและใช้ภาษาอะไร การเริ่มงานโดยไม่รู้ปลายทางมักทำให้ต้องแปลซ้ำและเสียค่าธรรมเนียมนิติกรณ์ซ้ำ · หากมีเอกสารชุดเดียวกันหลายฉบับ ให้ส่งพร้อมกันเพื่อคุมการสะกดชื่อและศัพท์ให้เหมือนกันทั้งชุด · " +
      CTA,
    synonyms: [
      "เตรียมเอกสารส่งแปล",
      "what to prepare before translation thailand",
      "สแกนเอกสารส่งแปลอย่างไร",
    ],
    cluster: "process",
  },
  {
    slug: "mlt-w2-แปลกับ-naati-ต่างกัน",
    q: "คำแปลรับรองในไทย ต่างจากคำแปล NAATI อย่างไร?",
    aShort:
      "คำแปลรับรองในไทยเน้นให้หน่วยงานไทยและสถานทูตในไทยยอมรับผ่านสายนิติกรณ์ ส่วน NAATI เป็นระบบรับรองนักแปลของออสเตรเลีย ซึ่งหน่วยงานออสเตรเลียและนิวซีแลนด์หลายแห่งกำหนดโดยเฉพาะ",
    aLong:
      "เลือกตามปลายทาง: ยื่นในไทย/ยุโรป/ตะวันออกกลาง → คำแปลรับรอง + นิติกรณ์กรมการกงสุล (+ สถานทูตถ้ากำหนด) · ยื่นวีซ่า การศึกษา หรือขอรับรองวิชาชีพในออสเตรเลีย → มักต้องคำแปลโดยนักแปล NAATI · บางกรณีต้องใช้ทั้งสองแบบสำหรับเอกสารคนละชุด · " +
      CTA,
    synonyms: [
      "แปลรับรองไทย vs naati",
      "naati vs thai certified translation",
      "ยื่นออสเตรเลียใช้คำแปลแบบไหน",
    ],
    cluster: "compare",
  },
];

export const MLT_QA_WAVE2_TOTAL = MLT_QA_WAVE2.length;
